/**
 * QUIPWA — erase this device's copy of the user's data
 *
 * Used when signing out with "erase this device" and when switching to a
 * different Google account. The user's Drive is never touched: everything
 * comes back on the next sign-in with the same account.
 *
 * Device preferences that are not personal data (language, theme, reading
 * preferences, keyboard shortcuts, editor layout) are kept.
 */
import { closeAllCollections } from '../db/collection-db-service.ts'
import type { AppServices } from '../app-services.ts'

const SQLITE_IDB_NAME = 'QUIPWA-sqljs'

/** localStorage keys holding personal data or per-account bookmarks. */
const PERSONAL_KEYS = [
  'quipwa:owner',
  'quipwa:auth',
  'quipwa:note-conflicts',
  'quipwa:cardsIndex',
  'quipwa:cardsIndex.dirty',
  'QUIPWA_root_folder_id',
  'wf_state_file_id',
  'wf_state_modified',
  'theme_profiles_file_id',
  'user_settings',
  // So the erased device starts at onboarding again, like a fresh install.
  'onboarding_completed',
  // The exam's API key is the user's money: another account on this device
  // must not inherit it. The provider and model choice (quipwa.ai.config) stay.
  'quipwa.ai.key',
]

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error ?? new Error(`Could not delete ${name}`))
    // Another tab still has it open; it is deleted as soon as that tab closes it.
    req.onblocked = () => resolve()
  })
}

export async function wipeLocalData(services: AppServices | null, storage: Storage = localStorage): Promise<void> {
  await services?.close()
  await closeAllCollections()
  await deleteDatabase(SQLITE_IDB_NAME)
  for (const key of PERSONAL_KEYS) storage.removeItem(key)
  try {
    sessionStorage.removeItem('quipwa.ai.key')
  } catch {
    // No session storage (tests, blocked site data): nothing was kept there.
  }
}
